import { SelectMatterInsuranceFormField } from "@/components/features/matter/settlements/SelectMatterInsuranceFormField";
import { LinkedMatterMembershipPopover } from "@/components/features/matter/shared/LinkedMatterMembershipPopover";
import { RelatedMatterObjectDeleteModal } from "@/components/features/matter/shared/RelatedMatterObjectDeleteModal";
import { VehicleCopyModeModal } from "@/components/features/matter/vehicles/VehicleCopyModeModal";
import {
  ActionConfirmationModal,
  Button,
  CheckboxFormField,
  Form,
  FormSubmitButton,
  SimpleSelectFormField,
  StateSelectFormField,
  SwitchFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { NumericFormField } from "@/components/ui/FormFields/NumericFormField/NumericFormField";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterVehicle,
  useDeleteMatterContactConnection,
  useSyncVehicleContactConnections,
  useUpdateMatterVehicle,
} from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCopyPartyVehicleToRelatedMatters,
  useUnlinkObjectFromMatter,
} from "@/lib/data/react-query/matters/matter-related-object";
import type { VehicleRelatedMatterPlacement } from "@/lib/data/requests/matters/matter-vehicle";
import { ContactConnectionInFormSchema } from "@/lib/data/schemas";
import { MatterVehicleSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { queryKeys } from "@/lib/data/static";
import { MATTER_CONTACT_CONNECTION_DEFAULT_VALUES } from "@/lib/data/static/formStatics";
import { MATTER_VEHICLE_DAMAGE_SEVERITY_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type {
  APIResponse,
  Contact,
  Connection,
  MatterContactConnection,
  MatterVehicle,
  MatterVehicleData,
} from "@/lib/types";
import {
  compareMatterDefendantConnectionsDisplayOrder,
  getlabel,
  getMatterContactConnectionLinkedId,
  getMatterContactConnections,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";

import { cn } from "@/lib/utils/cn";
import {
  createVehicleContactConnectionChildInput,
  getVehicleConnectionFormContact,
  getVehicleConnectionPreviewContact,
  getVehicleContactConnection,
  getVehicleContactConnections,
} from "@/lib/utils/connections/vehicle-contact-connections";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { getRelatedMatterSelectionOptions } from "@/lib/utils/relatedMatters";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import { isNil, omit, omitBy } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

const getVehicleYearsToCurrentYear = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  let startYear = 1908;
  while (startYear <= currentYear) {
    years.push(startYear);
    startYear++;
  }
  return years.reverse();
};
interface AddOrEditVehicleModalProps {
  editing: boolean;
  matterId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  setSelectedVehicleId?: (vehicleId: string) => void;
  vehicleId?: string;
  title: string;
  description?: string;
  enforceRequiredContact?: "Client" | "Defendant";
  initialData?: Partial<MatterVehicle>;
  defendantContactIdContext?: string;
  /** Matter contact id that must be linked as Driver or Passenger (other-party card). */
  requiredVehiclePartyContactId?: string;
  vehiclePartyRole?: "Client" | "Defendant" | "OtherParty";
  sourceMatterContactConnectionId?: string;
}

function vehicleMatterContactRolePreview(
  contact: Contact,
  matterId: string,
  label: string,
  connection: Connection | undefined,
  onRemoveFromForm?: () => void,
) {
  const previewContact =
    getVehicleConnectionPreviewContact(connection) ?? contact;

  return (
    <MatterContactConnectionModalPreview
      contactId={contact.id}
      previewContact={previewContact}
      fetchContactOnEdit={Boolean(connection)}
      matterId={matterId}
      matterContactConnection={
        {
          contact: previewContact,
          contactId: contact.id,
          label,
        } as MatterContactConnection
      }
      onRemoveContact={
        onRemoveFromForm
          ? () => {
              onRemoveFromForm();
            }
          : undefined
      }
    />
  );
}

const createVehicleFormSchema = (
  enforceRequiredContact?: "Client" | "Defendant",
  clientContactId?: string,
  defendantContactIds: string[] = [],
  requiredPartyContactId?: string,
) =>
  MatterVehicleSchema.pick({
    insuranceId: true,
    color: true,
    damageSeverity: true,
    description: true,
    licensePlateNumber: true,
    registrationState: true,
    make: true,
    mileage: true,
    model: true,
    totalLoss: true,
    repairEstimate: true,
    vin: true,
    year: true,
    deductiblePaidAmount: true,
    deductibleReimbursed: true,
    lossOfUse: true,
    lossOfUseAmount: true,
    lossOfUseDetails: true,
    lossOfUseReimbursed: true,
  })
    .extend({
      matterContactConnectionRegisteredOwner: ContactConnectionInFormSchema,
      matterContactConnectionDriver: ContactConnectionInFormSchema,
      matterContactConnectionAutoShop: ContactConnectionInFormSchema.array(),
      matterContactConnectionPassenger: ContactConnectionInFormSchema.array(),
      matterContactConnectionPedestrian: ContactConnectionInFormSchema.array(),
    })
    .superRefine((data, ctx) => {
      const trimmedRequiredParty = requiredPartyContactId?.trim();
      let requiredContactIds: string[] = [];
      let driverPassengerRequirementMessage: string | undefined;

      if (trimmedRequiredParty) {
        requiredContactIds = [trimmedRequiredParty];
        driverPassengerRequirementMessage =
          "This contact must be selected as either Driver or Passenger.";
      } else if (enforceRequiredContact) {
        if (enforceRequiredContact === "Client" && clientContactId) {
          requiredContactIds = [clientContactId];
        } else if (enforceRequiredContact === "Defendant") {
          requiredContactIds = defendantContactIds;
        }
        if (requiredContactIds.length > 0) {
          driverPassengerRequirementMessage = `${enforceRequiredContact} must be selected as either Driver or Passenger`;
        }
      }

      if (requiredContactIds.length === 0) {
        return;
      }

      const driverContactId = data.matterContactConnectionDriver?.id;
      const passengerContactIds = data.matterContactConnectionPassenger
        .map((p) => p?.id)
        .filter(Boolean);

      const hasRequiredAsDriver = requiredContactIds.includes(
        driverContactId || "",
      );
      const hasRequiredAsPassenger = requiredContactIds.some((requiredId) =>
        passengerContactIds.includes(requiredId),
      );

      if (!hasRequiredAsDriver && !hasRequiredAsPassenger) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            driverPassengerRequirementMessage ||
            "This contact must be selected as either Driver or Passenger.",
          path: ["matterContactConnectionDriver"],
        });
      }
    });

const vehicleDefaultFormValues = {
  insuranceId: null,
  color: "",
  damageSeverity: null,
  description: "",
  licensePlateNumber: "",
  registrationState: null,
  repairEstimate: null,
  make: "",
  mileage: null,
  model: "",
  totalLoss: null,
  vin: "",
  year: null,
  deductiblePaidAmount: null,
  deductibleReimbursed: false,
  lossOfUse: false,
  lossOfUseAmount: null,
  lossOfUseDetails: "",
  lossOfUseReimbursed: false,
  matterContactConnectionRegisteredOwner:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionDriver: MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionAutoShop: [],
  matterContactConnectionPassenger: [],
  matterContactConnectionPedestrian: [],
};

export const AddOrEditVehicleModal = ({
  editing,
  title,
  description,
  matterId,
  open,
  setOpen,
  setSelectedVehicleId,
  vehicleId,
  enforceRequiredContact,
  initialData,
  defendantContactIdContext,
  requiredVehiclePartyContactId,
  vehiclePartyRole,
  sourceMatterContactConnectionId,
}: AddOrEditVehicleModalProps) => {
  const queryClient = useQueryClient();
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const vehicleYears = getVehicleYearsToCurrentYear();
  const { updateSlideoverPayload } = useModals();
  const matter = matterData.data;
  const contactConnections = getMatterContactConnections(matter);
  const vehicle = matter.vehicles.find((vehicle) => vehicle.id === vehicleId);
  const vehicleInitialValues = mergeInitialData(vehicle, initialData);
  const findRoles = (label: string) => {
    return contactConnections.filter(
      (connection) =>
        getMatterContactConnectionLinkedId(connection, "vehicleId") ===
          vehicleInitialValues?.id && getlabel(connection) === label,
    );
  };

  const clientConnection = contactConnections.find(
    (connection) => getlabel(connection) === "Client",
  );
  const defendantConnection = useMemo(
    () =>
      contactConnections
        .filter((connection) => getlabel(connection) === "Defendant")
        .sort(compareMatterDefendantConnectionsDisplayOrder),
    [contactConnections],
  );
  const clientContact = clientConnection?.contact;
  const defendantContacts = useMemo(
    () => defendantConnection.map((connection) => connection.contact),
    [defendantConnection],
  );
  const contextContactFromMatter = defendantContactIdContext
    ? contactConnections.find(
        (connection) => connection.contactId === defendantContactIdContext,
      )?.contact
    : undefined;
  const defendantsForAssign = useMemo(() => {
    if (!defendantContactIdContext) {
      return defendantContacts;
    }
    return contextContactFromMatter ? [contextContactFromMatter] : [];
  }, [defendantContactIdContext, defendantContacts, contextContactFromMatter]);
  const defendantContactIdsForSchema = defendantContactIdContext
    ? [defendantContactIdContext]
    : defendantContacts.map((defendant) => defendant.id);

  const registeredOwner = getVehicleContactConnection(
    vehicle,
    "Registered Owner",
  );
  const driver = getVehicleContactConnection(vehicle, "Driver");
  const autoShops = getVehicleContactConnections(vehicle, "Auto Shop");
  const passengers = getVehicleContactConnections(vehicle, "Passenger");
  const legacyPedestrians = findRoles("Pedestrian");
  const pedestrians = getVehicleContactConnections(vehicle, "Pedestrian");

  const clientContactId = clientContact?.id;
  const vehicleFormSchema = createVehicleFormSchema(
    enforceRequiredContact,
    clientContactId,
    defendantContactIdsForSchema,
    requiredVehiclePartyContactId,
  );

  /**
   * Snapshot for opening the shell — not wired as RHF `values`.
   * Vehicle and contact mutations optimistically patch the matter cache; if the form
   * were controlled from matter while `matterContactConnection` is still catching up,
   * RHF would re-sync and show stale contact chips under the loading overlay.
   */
  const getFormValuesFromMatterSnapshot = (): z.infer<
    typeof vehicleFormSchema
  > => {
    if (editing && vehicle) {
      return {
        ...vehicleDefaultFormValues,
        ...(vehicleInitialValues ?? {}),
        matterContactConnectionRegisteredOwner:
          getVehicleConnectionFormContact(registeredOwner),
        matterContactConnectionDriver: getVehicleConnectionFormContact(driver),
        matterContactConnectionAutoShop: autoShops.map(
          getVehicleConnectionFormContact,
        ),
        matterContactConnectionPassenger: passengers.map(
          getVehicleConnectionFormContact,
        ),
        matterContactConnectionPedestrian: pedestrians.map(
          getVehicleConnectionFormContact,
        ),
      };
    }
    const trimmedRequiredParty = requiredVehiclePartyContactId?.trim();
    const partyConnectionRowForDefaultDriver =
      trimmedRequiredParty &&
      contactConnections.find(
        (c) =>
          c.contactId === trimmedRequiredParty &&
          !getMatterContactConnectionLinkedId(c, "vehicleId"),
      );

    return {
      ...vehicleDefaultFormValues,
      ...(initialData ?? {}),
      ...(partyConnectionRowForDefaultDriver
        ? {
            matterContactConnectionDriver:
              partyConnectionRowForDefaultDriver.contact,
          }
        : {}),
    };
  };

  const matterSnapshotFormValuesRef = useRef(getFormValuesFromMatterSnapshot);
  matterSnapshotFormValuesRef.current = getFormValuesFromMatterSnapshot;

  const vehicleForm = useForm<z.infer<typeof vehicleFormSchema>>({
    resolver: zodResolver(vehicleFormSchema),
    defaultValues: getFormValuesFromMatterSnapshot(),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const { hasActionPermission, hasGrant } = useUser();

  const createVehicle = useCreateMatterVehicle({ showSuccessToast: false });
  const updateVehicle = useUpdateMatterVehicle({ showSuccessToast: false });
  const unlinkVehicle = useUnlinkObjectFromMatter({
    objectLabel: "Vehicle",
  });
  const deleteMatterContactConnection = useDeleteMatterContactConnection({
    showSuccessToast: false,
    showErrorToast: true,
  });
  const syncVehicleConnections = useSyncVehicleContactConnections();
  const copyPartyVehicle = useCopyPartyVehicleToRelatedMatters({
    showToast: false,
  });

  const [, setSelectedRegisteredOwner] = useState<Contact | null>(null);
  const [, setSelectedDriver] = useState<Contact | null>(null);
  const [, setSelectedAutoShops] = useState<Contact[]>([]);
  const [, setSelectedPassengers] = useState<Contact[]>([]);
  const [, setSelectedPedestrians] = useState<Contact[]>([]);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const [isVehicleCopyModeOpen, setIsVehicleCopyModeOpen] = useState(false);
  const [selectedRelatedMatterIds, setSelectedRelatedMatterIds] = useState<
    string[]
  >([]);
  const relatedMatterOptions = useMemo(
    () => getRelatedMatterSelectionOptions(matter),
    [matter],
  );
  const canCopyToRelatedMatters =
    !editing &&
    Boolean(vehiclePartyRole && sourceMatterContactConnectionId) &&
    relatedMatterOptions.length > 0 &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterVehicle,
      ACTION_PERMISSION_VALUES.create,
    ) &&
    hasGrant(ACTION_PERMISSION_KEYS.matterVehicle, "view") &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterVehicle,
      ACTION_PERMISSION_VALUES.update,
    );

  /** Covers the full save handler (vehicle + connections + follow-up UI), not only mutation `isPending` (which clears between phases and misreports parallel connection calls). */
  const [isVehicleSaveInFlight, setIsVehicleSaveInFlight] = useState(false);

  const onSubmit = async (
    data: z.infer<typeof vehicleFormSchema>,
    placements: VehicleRelatedMatterPlacement[] = [],
    shouldCloseAfterSave = closeAfterSave,
  ) => {
    setIsVehicleSaveInFlight(true);
    let resData: APIResponse<MatterVehicleData>;
    try {
      const normalizedData = omit(
        {
          ...data,
          insuranceId: data.insuranceId || undefined,
        },
        [
          "matterContactConnectionRegisteredOwner",
          "matterContactConnectionDriver",
          "matterContactConnectionAutoShop",
          "matterContactConnectionPassenger",
          "matterContactConnectionPedestrian",
        ],
      );
      if (editing && vehicle) {
        const result = await updateVehicle.mutateAsync({
          id: vehicle.id,
          matterId: matter.id,
          dateModified: vehicle.dateModified,
          ...normalizedData,
        });
        resData = result;
      } else {
        const connections = [
          ...(data.matterContactConnectionRegisteredOwner?.id
            ? [
                createVehicleContactConnectionChildInput(
                  data.matterContactConnectionRegisteredOwner.id,
                  "Registered Owner",
                ),
              ]
            : []),
          ...(data.matterContactConnectionDriver?.id
            ? [
                createVehicleContactConnectionChildInput(
                  data.matterContactConnectionDriver.id,
                  "Driver",
                ),
              ]
            : []),
          ...data.matterContactConnectionAutoShop.flatMap((contact) =>
            contact?.id
              ? [
                  createVehicleContactConnectionChildInput(
                    contact.id,
                    "Auto Shop",
                  ),
                ]
              : [],
          ),
          ...data.matterContactConnectionPassenger.flatMap((contact) =>
            contact?.id
              ? [
                  createVehicleContactConnectionChildInput(
                    contact.id,
                    "Passenger",
                  ),
                ]
              : [],
          ),
          ...data.matterContactConnectionPedestrian.flatMap((contact) =>
            contact?.id
              ? [
                  createVehicleContactConnectionChildInput(
                    contact.id,
                    "Pedestrian",
                  ),
                ]
              : [],
          ),
        ];
        const result = await createVehicle.mutateAsync({
          matterId: matter.id,
          ...normalizedData,
          connections,
        });
        resData = result;
        updateSlideoverPayload("vehicle", {
          editing: true,
          vehicleId: result.data.id,
          title: SLIDEOVER_TITLE.VEHICLE.UPDATE,
          description: SLIDEOVER_DESCRIPTION.VEHICLE.UPDATE,
        });
      }

      if (editing) {
        const legacyPedestrianIds = new Set(
          legacyPedestrians.map((connection) => connection.contactId),
        );
        await syncVehicleConnections.mutateAsync({
          currentVehicle: vehicle,
          desiredContactIdsByRole: {
            "Auto Shop": data.matterContactConnectionAutoShop.flatMap(
              (contact) => (contact?.id ? [contact.id] : []),
            ),
            Driver: data.matterContactConnectionDriver?.id
              ? [data.matterContactConnectionDriver.id]
              : [],
            Passenger: data.matterContactConnectionPassenger.flatMap(
              (contact) => (contact?.id ? [contact.id] : []),
            ),
            "Registered Owner": [
              ...(data.matterContactConnectionRegisteredOwner?.id
                ? [data.matterContactConnectionRegisteredOwner.id]
                : []),
              ...getVehicleContactConnections(vehicle, "Registered Owner")
                .slice(1)
                .map((connection) => connection.targetObjectId),
            ],
            Pedestrian: data.matterContactConnectionPedestrian.flatMap(
              (contact) =>
                contact?.id && !legacyPedestrianIds.has(contact.id)
                  ? [contact.id]
                  : [],
            ),
          },
          matterId: matter.id,
          vehicleId: resData.data.id,
        });

        const desiredPedestrianIds = new Set(
          data.matterContactConnectionPedestrian.flatMap((contact) =>
            contact?.id ? [contact.id] : [],
          ),
        );
        await Promise.all([
          ...legacyPedestrians
            .filter(
              (connection) => !desiredPedestrianIds.has(connection.contactId),
            )
            .map((connection) =>
              deleteMatterContactConnection.mutateAsync({ id: connection.id }),
            ),
        ]);
      }

      if (placements.length > 0) {
        if (!sourceMatterContactConnectionId) {
          throw new Error("Vehicle copy placement is required.");
        }
        await copyPartyVehicle.mutateAsync({
          vehicleId: resData.data.id,
          sourceMatterContactConnectionId,
          sourceMatterId: matter.id,
          placements,
        });
      }

      await queryClient.refetchQueries({
        queryKey: [queryKeys.matters, matter.id],
      });

      toast.success(editing ? "Vehicle updated." : "Vehicle added.");

      if (shouldCloseAfterSave) {
        setOpen(false);
        setSelectedVehicleId?.(resData.data.id);
        setSelectedRegisteredOwner(null);
        setSelectedDriver(null);
        setSelectedAutoShops([]);
        setSelectedPassengers([]);
        setSelectedPedestrians([]);
        vehicleForm.reset({
          ...vehicleDefaultFormValues,
          ...omitBy(resData.data, isNil),
          // Submitted contact slots — matter-derived roles can still be stale until refetch.
          matterContactConnectionRegisteredOwner:
            data.matterContactConnectionRegisteredOwner,
          matterContactConnectionDriver: data.matterContactConnectionDriver,
          matterContactConnectionAutoShop: data.matterContactConnectionAutoShop,
          matterContactConnectionPassenger:
            data.matterContactConnectionPassenger,
          matterContactConnectionPedestrian:
            data.matterContactConnectionPedestrian,
        });
      } else {
        updateSlideoverPayload("vehicle", {
          editing: true,
          vehicleId: resData.data.id,
          title: SLIDEOVER_TITLE.VEHICLE.UPDATE,
          description: SLIDEOVER_DESCRIPTION.VEHICLE.UPDATE,
        });
        vehicleForm.reset({
          ...vehicleDefaultFormValues,
          ...omitBy(resData.data, isNil),
          matterContactConnectionRegisteredOwner:
            data.matterContactConnectionRegisteredOwner,
          matterContactConnectionDriver: data.matterContactConnectionDriver,
          matterContactConnectionAutoShop: data.matterContactConnectionAutoShop,
          matterContactConnectionPassenger:
            data.matterContactConnectionPassenger,
          matterContactConnectionPedestrian:
            data.matterContactConnectionPedestrian,
        });
        setSelectedRegisteredOwner(null);
        setSelectedDriver(null);
        setSelectedAutoShops([]);
        setSelectedPassengers([]);
        setSelectedPedestrians([]);
      }
    } finally {
      setIsVehicleSaveInFlight(false);
    }
  };

  const handleUnlink = async () => {
    if (!editing || !vehicle) return;
    await unlinkVehicle.mutateAsync({
      matterId: matter.id,
      objectId: vehicle.id,
      objectType: "vehicle",
    });
    setOpen(false);
  };

  const isFormActuallyDirty =
    Object.keys(vehicleForm.formState.dirtyFields).length > 0;

  useEffect(() => {
    const clearSelectedConnections = () => {
      setSelectedRegisteredOwner(null);
      setSelectedDriver(null);
      setSelectedAutoShops([]);
      setSelectedPassengers([]);
      setSelectedPedestrians([]);
    };
    if (!open) {
      vehicleForm.reset();
      clearSelectedConnections();
      return;
    }
    vehicleForm.reset(matterSnapshotFormValuesRef.current());
    clearSelectedConnections();
  }, [
    open,
    vehicleId,
    editing,
    vehicleForm,
    requiredVehiclePartyContactId,
    driver?.id,
  ]);

  // Determine if any loading state is active
  const isFormLoading =
    isVehicleSaveInFlight ||
    createVehicle.isPending ||
    updateVehicle.isPending ||
    unlinkVehicle.isPending ||
    deleteMatterContactConnection.isPending ||
    syncVehicleConnections.isPending ||
    copyPartyVehicle.isPending;

  const location = useLocation();

  const isOnClientsPage = location.pathname.includes("/client");
  const isOnDefendantsPage = location.pathname.includes("/defendant");
  const insuranceFilterLabel =
    enforceRequiredContact ??
    (isOnClientsPage ? "Client" : isOnDefendantsPage ? "Defendant" : undefined);

  const vehicleModalFormId = "matter-vehicle-modal-form";

  const vehicleModalContactPickerProps = {
    contactPickerPresentation: "modal" as const,
    contactSelectorSize: "compact" as const,
    hideCompactContactChrome: true,
  };

  const vehicleModalContactSidebar = (
    <div className="flex flex-col gap-2">
      <AddContactFormField
        {...vehicleModalContactPickerProps}
        label="Registered owner"
        register={vehicleForm.register(
          "matterContactConnectionRegisteredOwner",
        )}
        setSelectedContact={setSelectedRegisteredOwner}
        onDelete={() => {
          vehicleForm.setValue(
            "matterContactConnectionRegisteredOwner",
            MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
            { shouldDirty: true },
          );
          setSelectedRegisteredOwner(null);
        }}
        showSelectClientButton={isOnClientsPage}
        client={clientContact}
        showSelectDefendantButton={
          defendantConnection.length > 0 && isOnDefendantsPage
        }
        defendants={defendantsForAssign}
        matterId={matter.id}
        renderSelectedContactPreview={(contact) =>
          vehicleMatterContactRolePreview(
            contact,
            matter.id,
            "Registered Owner",
            registeredOwner?.targetObjectId === contact.id
              ? registeredOwner
              : undefined,
            () => {
              vehicleForm.setValue(
                "matterContactConnectionRegisteredOwner",
                MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                { shouldDirty: true },
              );
              setSelectedRegisteredOwner(null);
            },
          )
        }
      />
      <AddContactFormField
        {...vehicleModalContactPickerProps}
        label="Driver"
        register={vehicleForm.register("matterContactConnectionDriver")}
        setSelectedContact={setSelectedDriver}
        onDelete={() => {
          vehicleForm.setValue(
            "matterContactConnectionDriver",
            MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
            { shouldDirty: true },
          );
          setSelectedDriver(null);
        }}
        showSelectClientButton={isOnClientsPage}
        client={clientContact}
        showSelectDefendantButton={
          defendantConnection.length > 0 && isOnDefendantsPage
        }
        defendants={defendantsForAssign}
        matterId={matter.id}
        renderSelectedContactPreview={(contact) =>
          vehicleMatterContactRolePreview(
            contact,
            matter.id,
            "Driver",
            driver?.targetObjectId === contact.id ? driver : undefined,
            () => {
              vehicleForm.setValue(
                "matterContactConnectionDriver",
                MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                { shouldDirty: true },
              );
              setSelectedDriver(null);
            },
          )
        }
      />
      <AddContactFormField
        {...vehicleModalContactPickerProps}
        label="Passenger"
        register={vehicleForm.register("matterContactConnectionPassenger")}
        setSelectedContact={(contact) => {
          setSelectedPassengers((prev) => [...prev, contact]);
        }}
        onDelete={(contact) => {
          vehicleForm.setValue(
            "matterContactConnectionPassenger",
            vehicleForm
              .getValues("matterContactConnectionPassenger")
              .filter((p) => p && p.id !== contact.id),
            { shouldDirty: true },
          );
          setSelectedPassengers((prev) =>
            prev.filter((selectedContact) => selectedContact.id !== contact.id),
          );
        }}
        allowMultiple
        matterId={matter.id}
        showSelectClientButton={isOnClientsPage}
        client={clientContact}
        defendants={defendantsForAssign}
        renderSelectedContactPreview={(contact) =>
          vehicleMatterContactRolePreview(
            contact,
            matter.id,
            "Passenger",
            passengers.find((c) => c.targetObjectId === contact.id),
            () => {
              vehicleForm.setValue(
                "matterContactConnectionPassenger",
                vehicleForm
                  .getValues("matterContactConnectionPassenger")
                  .filter((p) => p && p.id !== contact.id),
                { shouldDirty: true },
              );
              setSelectedPassengers((prev) =>
                prev.filter(
                  (selectedContact) => selectedContact.id !== contact.id,
                ),
              );
            },
          )
        }
      />
      <AddContactFormField
        {...vehicleModalContactPickerProps}
        label="Auto shop"
        register={vehicleForm.register("matterContactConnectionAutoShop")}
        setSelectedContact={(contact) => {
          setSelectedAutoShops((prev) => [...prev, contact]);
        }}
        onDelete={(contact) => {
          vehicleForm.setValue(
            "matterContactConnectionAutoShop",
            vehicleForm
              .getValues("matterContactConnectionAutoShop")
              .filter((p) => p && p.id !== contact.id),
            { shouldDirty: true },
          );
          setSelectedAutoShops((prev) =>
            prev.filter((selectedContact) => selectedContact.id !== contact.id),
          );
        }}
        allowMultiple
        defaultSearchObject={{
          organizationSubTypes: ["Auto Shop"],
        }}
        matterId={matter.id}
        defaultSearchField="name"
        client={clientContact}
        defendants={defendantsForAssign}
        renderSelectedContactPreview={(contact) =>
          vehicleMatterContactRolePreview(
            contact,
            matter.id,
            "Auto Shop",
            autoShops.find((c) => c.targetObjectId === contact.id),
            () => {
              vehicleForm.setValue(
                "matterContactConnectionAutoShop",
                vehicleForm
                  .getValues("matterContactConnectionAutoShop")
                  .filter((p) => p && p.id !== contact.id),
                { shouldDirty: true },
              );
              setSelectedAutoShops((prev) =>
                prev.filter(
                  (selectedContact) => selectedContact.id !== contact.id,
                ),
              );
            },
          )
        }
      />
      <AddContactFormField
        {...vehicleModalContactPickerProps}
        label="Pedestrian"
        register={vehicleForm.register("matterContactConnectionPedestrian")}
        setSelectedContact={(contact) => {
          setSelectedPedestrians((prev) => [...prev, contact]);
        }}
        onDelete={(contact) => {
          vehicleForm.setValue(
            "matterContactConnectionPedestrian",
            vehicleForm
              .getValues("matterContactConnectionPedestrian")
              .filter((p) => p && p.id !== contact.id),
            { shouldDirty: true },
          );
          setSelectedPedestrians((prev) =>
            prev.filter((selectedContact) => selectedContact.id !== contact.id),
          );
        }}
        allowMultiple
        matterId={matter.id}
        client={clientContact}
        defendants={defendantsForAssign}
        renderSelectedContactPreview={(contact) =>
          vehicleMatterContactRolePreview(
            contact,
            matter.id,
            "Pedestrian",
            pedestrians.find((c) => c.targetObjectId === contact.id),
            () => {
              vehicleForm.setValue(
                "matterContactConnectionPedestrian",
                vehicleForm
                  .getValues("matterContactConnectionPedestrian")
                  .filter((p) => p && p.id !== contact.id),
                { shouldDirty: true },
              );
              setSelectedPedestrians((prev) =>
                prev.filter(
                  (selectedContact) => selectedContact.id !== contact.id,
                ),
              );
            },
          )
        }
      />
    </div>
  );

  const vehicleDetailFields = (
    <>
      <SimpleSelectFormField
        items={vehicleYears}
        label="Year"
        register={vehicleForm.register("year")}
        placeholder="Select year"
        clearable
      />
      <TextFormField label="Make*" register={vehicleForm.register("make")} />
      <TextFormField label="Model" register={vehicleForm.register("model")} />
      <SelectMatterInsuranceFormField
        label="Insurance"
        register={vehicleForm.register("insuranceId")}
        matter={matter}
        contactConnectionLabel={insuranceFilterLabel}
        partyContactId={requiredVehiclePartyContactId}
        excludedInsuranceTypes={["Health"]}
        filterToParty={false}
      />
      <TextFormField label="Color" register={vehicleForm.register("color")} />
      <TextFormField label="VIN" register={vehicleForm.register("vin")} />
      <TextFormField
        label="License plate number"
        register={vehicleForm.register("licensePlateNumber")}
      />
      <StateSelectFormField
        register={vehicleForm.register("registrationState")}
        label="License plate state"
        name="registrationState"
        country="United States"
      />
      <SimpleSelectFormField
        items={MATTER_VEHICLE_DAMAGE_SEVERITY_OPTIONS}
        label="Damage severity"
        placeholder="Select damage severity"
        register={vehicleForm.register("damageSeverity")}
        clearable
      />
      <NumericFormField
        label="Mileage"
        register={vehicleForm.register("mileage", {
          valueAsNumber: true,
        })}
      />
      <CurrencyFormField
        label="Repair estimate"
        register={vehicleForm.register("repairEstimate")}
      />
      <CurrencyFormField
        label="Total loss"
        register={vehicleForm.register("totalLoss")}
      />
      <div className="col-span-2">
        <TextAreaFormField
          label="Description"
          register={vehicleForm.register("description")}
        />
      </div>
      <CurrencyFormField
        label="Deductible paid amount"
        register={vehicleForm.register("deductiblePaidAmount")}
      />
      <CheckboxFormField
        label="Deductible reimbursed"
        register={vehicleForm.register("deductibleReimbursed")}
      />
      <SwitchFormField
        label={<div>Loss of use</div>}
        register={vehicleForm.register("lossOfUse")}
      />
      {vehicleForm.watch("lossOfUse") ? (
        <CurrencyFormField
          label="Loss of use amount"
          register={vehicleForm.register("lossOfUseAmount")}
        />
      ) : (
        <div />
      )}
      <CheckboxFormField
        label="Loss of use reimbursed"
        register={vehicleForm.register("lossOfUseReimbursed")}
      />
      <div className="col-span-2">
        <TextAreaFormField
          label="Loss of use details"
          register={vehicleForm.register("lossOfUseDetails")}
        />
      </div>
    </>
  );

  return (
    <>
      <Form {...vehicleForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          titleTrailing={
            editing && vehicle ? (
              <LinkedMatterMembershipPopover
                canUnlink={hasActionPermission(
                  ACTION_PERMISSION_KEYS.matterVehicle,
                  ACTION_PERMISSION_VALUES.delete,
                )}
                currentMatterId={matter.id}
                enabled={open}
                objectId={vehicle.id}
                objectType="vehicle"
                onUnlink={() => {
                  setIsUnlinking(true);
                }}
              />
            ) : undefined
          }
          description={description}
          size="4xl"
          loading={isFormLoading}
          isDirty={isFormActuallyDirty}
          preview={vehicleModalContactSidebar}
          previewLabel={null}
          bodyFormId={vehicleModalFormId}
          bodyFormOnSubmit={vehicleForm.handleSubmit((data) => onSubmit(data))}
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                {editing &&
                  hasActionPermission(
                    ACTION_PERMISSION_KEYS.matterVehicle,
                    ACTION_PERMISSION_VALUES.delete,
                  ) && (
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={unlinkVehicle.isPending}
                      onClick={() => {
                        setIsDeleting(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={isFormActuallyDirty}
                />
                <FormSubmitButton
                  submitFormId={vehicleModalFormId}
                  title={
                    canCopyToRelatedMatters
                      ? "Save to this & related matters"
                      : editing
                        ? "Save"
                        : "Add vehicle"
                  }
                  type={canCopyToRelatedMatters ? "button" : "submit"}
                  loading={isFormLoading}
                  submitFn={() => {
                    if (canCopyToRelatedMatters) {
                      const allRelatedMatterIds = relatedMatterOptions.map(
                        (option) => option.id,
                      );
                      setSelectedRelatedMatterIds(allRelatedMatterIds);
                      if (vehiclePartyRole === "Client") {
                        void vehicleForm.handleSubmit(() => {
                          setIsVehicleCopyModeOpen(true);
                        })();
                        return;
                      }
                      void vehicleForm.handleSubmit((data) =>
                        onSubmit(
                          data,
                          allRelatedMatterIds.map((matterId) => ({
                            matterId,
                            placement: "other_party",
                          })),
                          true,
                        ),
                      )();
                      return;
                    }
                    setCloseAfterSave(true);
                  }}
                  overrideDisabled={Boolean(initialData) && !editing}
                  disabled={isFormLoading || (Boolean(initialData) && !editing)}
                  additionalActions={
                    canCopyToRelatedMatters
                      ? [
                          {
                            label: "Save to this matter only",
                            onSelect: () => {
                              void vehicleForm.handleSubmit((data) =>
                                onSubmit(data, [], true),
                              )();
                            },
                          },
                        ]
                      : undefined
                  }
                />
              </div>
            </div>
          }
        >
          <fieldset
            className={cn(slideoverFieldsetClassName, "my-0 p-0")}
            disabled={
              !hasActionPermission(
                ACTION_PERMISSION_KEYS.matterVehicle,
                ACTION_PERMISSION_VALUES.update,
              )
            }
          >
            {vehicleDetailFields}
          </fieldset>
        </AppModal>
      </Form>
      <VehicleCopyModeModal
        destinations={relatedMatterOptions.filter(({ id }) =>
          selectedRelatedMatterIds.includes(id),
        )}
        open={isVehicleCopyModeOpen}
        onOpenChange={setIsVehicleCopyModeOpen}
        loading={isFormLoading}
        onSubmit={(placements) => {
          setIsVehicleCopyModeOpen(false);
          void vehicleForm.handleSubmit((data) =>
            onSubmit(data, placements, true),
          )();
        }}
      />
      {vehicle ? (
        <RelatedMatterObjectDeleteModal
          open={isDeleting}
          onOpenChange={setIsDeleting}
          currentMatterId={matter.id}
          objectId={vehicle.id}
          objectLabel="Vehicle"
          objectType="vehicle"
          onCurrentMatterRemoved={() => {
            setOpen(false);
          }}
        />
      ) : null}
      <ActionConfirmationModal
        title="Unlink this vehicle from the matter?"
        description="This removes the vehicle from this matter only. If this is its last matter, the vehicle will be deleted."
        buttonText="Unlink vehicle"
        open={isUnlinking && !!vehicle}
        setOpen={setIsUnlinking}
        afterActionFn={handleUnlink}
        isActionPending={unlinkVehicle.isPending}
      />
    </>
  );
};
